import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isNotFoundError } from '@/utils/apiError';
import { SERVER_ERROR_MESSAGE } from '@/constants/messages';

export const NETWORK_ERROR_EVENT = 'app-network-error';
export const NOT_FOUND_ERROR_EVENT = 'app-not-found-error';
export const SERVER_ERROR_EVENT = 'app-server-error';

const isServerError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' && status >= 500 && status < 600;
};

const handleServerError = (error: unknown, queryOrMutation: any) => {
  if (queryOrMutation?.meta?.suppressServerErrorModal) return;
  if (!isServerError(error)) return;

  window.dispatchEvent(
    new CustomEvent(SERVER_ERROR_EVENT, {
      detail: { message: SERVER_ERROR_MESSAGE }
    })
  );
};

const handleNetworkError = (error: unknown, queryOrMutation: any) => {
  if (queryOrMutation?.meta?.suppressNetworkToast) return;

  const isOffline = typeof window !== 'undefined' && !window.navigator.onLine;
  const isAxiosNetworkError =
    (error as any)?.code === 'ERR_NETWORK' ||
    (error as any)?.message === 'Network Error';

  if (isOffline || isAxiosNetworkError) {
    const networkMsg = 'インターネット接続エラーが発生しました。';
    const overrideMsg = queryOrMutation?.meta?.errorMessage as string | undefined;

    window.dispatchEvent(
      new CustomEvent(NETWORK_ERROR_EVENT, {
        detail: { message: isOffline ? networkMsg : (overrideMsg || networkMsg) }
      })
    );
  }
};

/**
 * Shared QueryClient Instance
 *
 * Centralized configuration for React Query.
 *
 * Global 404 handling:
 * When a mutation or query returns 404 (item deleted in another tab/session),
 * all active queries are invalidated so lists refresh automatically.
 * - MutationCache.onError: covers delete / update / create mutations.
 *   Also dispatches NOT_FOUND_ERROR_EVENT so GlobalBehaviorWrapper shows
 *   a "データが見つかりません。" toast to the user.
 * - QueryCache.onError: covers detail-fetch queries (e.g. clicking "Edit"
 *   on a deleted row). The erroring query itself is excluded from
 *   invalidation to prevent an infinite refetch loop.
 *   No toast is dispatched — pages render their own 404 UI.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: 60 * 60 * 1000, // 1 hour
      // gcTime: 24 * 60 * 60 * 1000, // 24 hours
      staleTime: 0, // Always refetch API
      gcTime: 0, // Immediately garbage collect unused queries 
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isNotFoundError(error)) {
        // Skip invalidation when mutation opts out (e.g. VisitClinicEditPage
        // wants to preserve local UI state like the selected main_massager).
        const meta = mutation?.meta as {
          suppressNotFoundToast?: boolean;
          suppressNotFoundInvalidate?: boolean;
        } | undefined;
        if (!meta?.suppressNotFoundInvalidate) {
          queryClient.invalidateQueries({
            predicate: query => !query.queryKey.includes('detail'),
          });
        }
        // Skip toast when mutation opts out (e.g. EditClinicLocationMassagerPage shows 404 UI)
        if (!meta?.suppressNotFoundToast) {
          window.dispatchEvent(new CustomEvent(NOT_FOUND_ERROR_EVENT, {
            detail: { message: 'データが見つかりません。' },
          }));
        }
      }
      handleNetworkError(error, mutation);
      handleServerError(error, mutation);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isNotFoundError(error)) {
        // Only invalidate LIST queries so they refresh (e.g. item deleted elsewhere).
        // Do NOT invalidate other detail/show queries - that causes refetch loops when
        // multiple detail queries 404 on the same page (e.g. EditClinicLocationMassagerPage).
        queryClient.invalidateQueries({
          predicate: q =>
            q.queryHash !== query.queryHash &&
            q.queryKey.some(k => k === 'list' || k === 'infinite-list'),
        });
        if (query.meta?.showNotFoundToast) {
          window.dispatchEvent(new CustomEvent(NOT_FOUND_ERROR_EVENT, {
            detail: { message: 'データが見つかりません。' },
          }));
        }
      }
      handleNetworkError(error, query);
      handleServerError(error, query);
    },
  }),
});

